import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

type PlanKey = "starter" | "growth" | "pro";

const PLANS: Record<
  PlanKey,
  {
    amount: number;
    credits: number;
    name: string;
  }
> = {
  starter: {
    name: "Starter",
    amount: 9,
    credits: 150,
  },
  growth: {
    name: "Growth",
    amount: 29,
    credits: 500,
  },
  pro: {
    name: "Pro",
    amount: 79,
    credits: 1500,
  },
};

export default function PurchaseCredits() {
  const navigate = useNavigate();
  const { token, user, refreshUser } = useAuth();

  const [selectedPlan, setSelectedPlan] =
    useState<PlanKey>("growth");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePayment = async () => {
    setError(null);
    setLoading(true);

    const plan = PLANS[selectedPlan];

    try {
      const orderRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/payments/create-order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            amount: plan.amount,
            credits: plan.credits,
          }),
        }
      );

      if (!orderRes.ok) {
        throw new Error("Failed to create payment order.");
      }

      const { id: order_id, amount } = await orderRes.json();

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID!,
        amount,
        currency: "USD",
        name: "Backlink Banana",
        description: `${plan.name} Plan`,
        order_id,

        handler: async (resp: any) => {
          try {
            const verifyRes = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/payments/verify-payment`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                  razorpay_order_id: resp.razorpay_order_id,
                  razorpay_payment_id: resp.razorpay_payment_id,
                  razorpay_signature: resp.razorpay_signature,
                }),
              }
            );

            const result = await verifyRes.json();

            if (!result.success) {
              throw new Error(
                result.message || "Verification failed."
              );
            }

            await refreshUser();

            navigate(
              `/payment-success?credits=${plan.credits}`
            );
          } catch (e: any) {
            setError(e.message);
          }
        },

        prefill: {
          name: user?.name || "",
          email: user?.email || "",
        },

        theme: {
          color: "#3399cc",
        },
      };

      new (window as any).Razorpay(options).open();
    } catch (e: any) {
      setError(e.message || "Payment initiation failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-100 to-white flex items-center justify-center p-6">
      <div className="w-full max-w-5xl space-y-8">
        <h2 className="text-3xl font-bold text-gray-800 text-center">
          Choose Your Plan
        </h2>

        {error && (
          <div className="text-red-600 bg-red-50 p-3 rounded">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {(Object.entries(PLANS) as [
            PlanKey,
            {
              amount: number;
              credits: number;
              name: string;
            }
          ][]).map(([key, plan]) => {
            const isSelected = selectedPlan === key;

            return (
              <div
                key={key}
                onClick={() => setSelectedPlan(key)}
                className={`relative cursor-pointer rounded-xl p-8 shadow-lg transition ${
                  isSelected
                    ? "border-2 border-blue-600 bg-white"
                    : "border border-gray-200 bg-white hover:shadow-xl"
                }`}
              >
                {key === "growth" && (
                  <span className="absolute top-4 right-4 bg-yellow-400 text-black text-xs font-bold px-2 py-1 rounded-full">
                    Most Popular
                  </span>
                )}

                {key === "pro" && (
                  <span className="absolute top-4 right-4 bg-indigo-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                    Best Value
                  </span>
                )}

                <h3 className="text-2xl font-bold text-gray-800 mb-3">
                  {plan.name}
                </h3>

                <p className="text-5xl font-extrabold text-gray-900">
                  ${plan.amount}
                </p>

                <p className="text-gray-600 mt-2 mb-6">
                  {plan.credits} Credits
                </p>

                <ul className="space-y-3 text-gray-700">
                  <li>✔ Access to backlink prospects</li>
                  <li>✔ Smart filtering</li>
                  <li>✔ Export results</li>
                  <li>✔ Email support</li>
                </ul>
              </div>
            );
          })}
        </div>

        <button
          onClick={handlePayment}
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-lg shadow disabled:opacity-50 transition"
        >
          {loading ? "Processing..." : "Purchase Plan"}
        </button>
      </div>
    </div>
  );
}